// import { Outlet } from "react-router";
import { GridContainer } from "../grid-container";
import { GridChild } from "../grid-child";
import {
  baseThemeSx,
  getLocalStorageItem,
  setLocalStorageItem,
} from "../../utils";
import { SettingsMenu } from "../settings-menu";
import { Box, Stack, Typography } from "@mui/material";
import { SidebarMenu } from "./sidebar-menu";
import { useUserContext } from "../../hooks/use-auth-context";
import smallLogo from "../../assets/logo-circles.png";
import { colors, styleConstants, skyTint, whiteTint } from "../../utils/colors";
import { Outlet } from "react-router";
import { useSiteSettings } from "../../hooks/use-site-settings";
import { useEffect, useRef, useState } from "react";
import { useSiteMedia } from "../../hooks/use-site-media";
import { useMultiSiteContext } from "../../hooks/use-site-context";
import { useCapabilities } from "@hooks/use-capabilities";
import { SmAiChatWidget } from "../sm-ai-chat";

const SITE_LOGO_STORAGE_KEY = "siteLogo";

export const SidebarLayout = () => {
  const { user } = useUserContext();
  const { can } = useCapabilities();
  const { siteSettings, getSiteSettings } = useSiteSettings();
  const { fetchMedia, loading: mediaLoading, media } = useSiteMedia();
  const { baseUrl } = useMultiSiteContext();

  const mediaStorageKey = `${SITE_LOGO_STORAGE_KEY}-${baseUrl}`;
  const storageImage = getLocalStorageItem(mediaStorageKey);
  const [siteLogo, setSiteLogo] = useState<string | null>(storageImage || null);

  const hasFetchedSettings = useRef(false);
  useEffect(() => {
    if (!hasFetchedSettings.current && user?.authToken && can("manage_options")) {
      hasFetchedSettings.current = true;
      getSiteSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.authToken, can]);

  const hasFetchedLogo = useRef(false);
  useEffect(() => {
    if (
      !mediaLoading &&
      (media.length ?? 0) === 0 &&
      siteSettings?.site_logo &&
      !hasFetchedLogo.current &&
      user?.authToken &&
      !siteLogo // Only fetch if siteLogo is not already set
    ) {
      hasFetchedLogo.current = true;
      fetchMedia(siteSettings?.site_logo)
        .then((mediaItem) => {
          if (mediaItem) {
            const mediaUrl = mediaItem.media_details.sizes.medium.source_url;
            setSiteLogo(mediaUrl);
            setLocalStorageItem(mediaStorageKey, mediaUrl);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch site logo media:", err);
        });
    }
  }, [
    fetchMedia,
    siteSettings,
    mediaLoading,
    media.length,
    user?.authToken,
    siteLogo,
    mediaStorageKey,
  ]);

  return (
    <GridContainer
      sx={{
        maxHeight: "100vh",
        justifyContent: "stretch",
      }}
      spacing={0}
      className="sidebar-layout"
    >
      <GridChild
        size={{ xs: 12, md: 3, lg: 2 }}
        className="sidebar-wrapper"
        sx={{
          background: `linear-gradient(180deg, ${colors.navy} 0%, ${colors.navyDeep} 100%)`,
          height: { xs: styleConstants.mobileSidebarHeight, md: "auto" },
          minHeight: {
            md: "100vh",
          },
          maxHeight: { xs: styleConstants.mobileSidebarHeight, md: "none" },
          position: { xs: "sticky", md: "static" },
          top: { xs: 0, md: "auto" },
          zIndex: { xs: 1103, md: "auto" },
          borderRight: { md: `1px solid ${whiteTint(0.06)}` },
        }}
      >
        <Stack
          direction="row"
          justifyContent={"space-between"}
          alignItems="center"
          sx={{
            height: styleConstants.mobileSidebarHeight,
            px: 1.5,
            borderBottom: { xs: 0, md: `1px solid ${whiteTint(0.08)}` },
          }}
        >
          <Box
            sx={{
              color: colors.surface,
              fontSize: "1rem",
              fontWeight: 700,
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: "9px",
                  bgcolor: skyTint(0.14),
                  flexShrink: 0,
                }}
              >
                <img
                  src={smallLogo}
                  alt="school-manager-icon"
                  style={{ height: 20, width: "auto", display: "block" }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontFamily: "'Montserrat', 'Roboto', sans-serif",
                  letterSpacing: "-0.01em",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.firstName} {user?.lastName}
              </Typography>
            </Stack>
          </Box>
          <SettingsMenu />
        </Stack>

        {siteLogo && (
          <Box
            pl={2}
            sx={{
              height: "44px",
              display: "flex",
              alignItems: "center",
              borderBottom: { md: `1px solid ${whiteTint(0.06)}` },
            }}
          >
            <img
              src={siteLogo}
              height="70%"
              width="auto"
              alt="instance logo"
              style={{ display: "block" }}
            />
          </Box>
        )}
        <Box
          className="desktop-sidebar-menu"
          sx={{
            display: { xs: "none", md: "block" },
            pt: 1.5,
          }}
        >
          <SidebarMenu />
        </Box>
      </GridChild>
      <GridChild
        size={{ xs: 12, md: 9, lg: 10 }}
        className="content-wrapper"
        sx={{ ...baseThemeSx, flexShrink: 1 }}
      >
        <Outlet />
      </GridChild>
      <SmAiChatWidget />
    </GridContainer>
  );
};
